import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ConfigManager } from "@/application/config/Config";
import { storageService } from "@/application/services/StorageService";
import { LoggerFactory } from "@/application/logging/Logger";

interface PaymentCompleteProps {
  canceled?: boolean;
  onFinish: (goTo?: string) => void;
}

export function PaymentComplete({
  canceled,
  onFinish
}: PaymentCompleteProps) {
  const [status, setStatus] = useState<"success" | "failed" | null>(null);
  const config = ConfigManager.getInstance();
  const logger = LoggerFactory.getLogger();

  useEffect(() => {
    const verifySubscription = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");

      if (!sessionId) {
        setStatus("failed");
        toast.error("Invalid payment session.");
        return;
      }

      if (canceled) {
        setStatus("failed");
        toast.error("Payment was canceled.");
        return;
      }

      try {
        const apiConfig = config.getApiConfig();
        const stripeBaseUrl = apiConfig.stripeBaseUrl || apiConfig.baseUrl;
        const response = await fetch(`${stripeBaseUrl}/stripe/confirm-subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.active) {
          setStatus("success");
          toast.success("Subscription verified");

          storageService.setItem("pro_subscription_active", "true");
          storageService.setItem("subscription_status", data.status);
          storageService.setItem("subscription_expires_at", data.currentPeriodEnd);
          storageService.setItem("subscription_interval", data.interval);
        } else {
          setStatus("failed");
          toast.error("Subscription is not active");
        }
      } catch (error) {
        logger.error("Subscription verification failed", error as Error, { sessionId });
        setStatus("failed");
        toast.error("Unable to verify subscription");
      }
    };

    verifySubscription();
  }, [canceled, config, logger]);

  const handleConfirm = () => {
    onFinish("home");
  };

  const handleRetry = () => {
    onFinish();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-50 p-6 z-[99999]">
      <div className="bg-white dark:bg-gray-900 shadow p-8 rounded-lg max-w-md w-full text-center">
        {status === null && (
          <p className="text-gray-600 dark:text-gray-300">
            Checking payment status...
          </p>
        )}

        {status === "success" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              Payment Successful
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Your subscription is now active.
            </p>

            <button
              onClick={handleConfirm}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              Continue
            </button>
          </>
        )}

        {status === "failed" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              Payment Failed
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Payment was not completed or subscription could not be verified.
            </p>

            <button
              onClick={handleRetry}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
            >
              Return
            </button>
          </>
        )}
      </div>
    </div>
  );
}
